import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useClosable from "../../hooks/useClosable";
import useRecentlyViewed from "../../hooks/useRecentlyViewed";
import { useLocationStore, usePipedriveStore, usePopupStore } from "../../index";
import DetailsType from "../../interfaces/DetailsType";
import { RecentItemType } from "../../interfaces/IRecentItem";
import { ClosableOrder } from "../../store/ClosableStore";
import { formatNumber, formatPrice, withHttps } from "../../utils/utils";
import ButtonWithTooltip from "../ButtonWithTooltip";
import commonStyles from "../Common.module.css";
import detailsStyles from "../Details.module.css";
import DetailsPanel from "../DetailsPanel";
import IconWithTooltip from "../IconWithTooltip";
import SegmentedButtons from "../SegmentedButtons";
import { CompanyFavoriteCheckbox } from "../favorites/FavoriteCheckbox";
import CompanyIcon from "../icons/CompanyIcon";
import DealIcon from "../icons/DealIcon";
import EditIcon from "../icons/EditIcon";
import LeadIcon from "../icons/LeadIcon";
import LeaseCompIcon from "../icons/LeaseCompIcon";
import LinkIcon from "../icons/LinkIcon";
import LinkedInIcon from "../icons/LinkedInIcon";
import LocationIcon from "../icons/LocationIcon";
import PipedriveIcon from "../icons/PipedriveIcon";
import SaleCompIcon from "../icons/SaleCompIcon";
import TenantInMarketIcon from "../icons/TenantInMarketIcon";
import OnMapSpinner from "../spinners/OnMapSpinner";
import CompanyDetailsDealsTab from "./CompanyDetailsDealsTab";
import CompanyDetailsLeadsTab from "./CompanyDetailsLeadsTab";
import CompanyDetailsLeaseCompsTab from "./CompanyDetailsLeaseCompsTab";
import CompanyDetailsLocationsTab from "./CompanyDetailsLocationsTab";
import CompanyDetailsSaleCompsTab from "./CompanyDetailsSaleCompsTab";
import CompanyDetailsTenantsInMarketTab from "./CompanyDetailsTenantsInMarketTab";

function CompanyDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const pipedriveStore = usePipedriveStore();
    const locationStore = useLocationStore();
    const popupStore = usePopupStore();
    const company = useMemo(() => computed(() => pipedriveStore.organizations.find(o => o.id.toString() === id)), [id]).get();
    const [companyTab, setCompanyTab] = useState(CompanyTab.Locations);
    const [locationCount, setLocationCount] = useState(0);
    const [dealCount, setDealCount] = useState(0);
    const [leadCount, setLeadCount] = useState(0);
    const [leaseCompCount, setLeaseCompCount] = useState(0);
    const [saleCompCount, setSaleCompCount] = useState(0);
    const [tenantInMarketCount, setTenantInMarketCount] = useState(0);
    const companyLogo = useMemo(() => pipedriveStore.organizationsLogos.find(l => l.organizationId === company?.id), [company?.id, pipedriveStore.organizationsLogos]);
    const segmentedButtons = useMemo(() => [
        { 
            value: CompanyTab.Locations, 
            label: (
                <IconWithTooltip title="Locations" count={locationCount}>
                    <LocationIcon color="#25479A" />
                </IconWithTooltip>
            )
        },
        { 
            value: CompanyTab.Deals, 
            label: (
                <IconWithTooltip title="Deals" count={dealCount}>
                    <DealIcon color="#25479A" />
                </IconWithTooltip>
            )
        },
        { 
            value: CompanyTab.LeaseComps, 
            label: (
                <IconWithTooltip title="Lease Comps" count={leaseCompCount}>
                    <LeaseCompIcon color="#25479A" />
                </IconWithTooltip>
            )
        },
        { 
            value: CompanyTab.SaleComps, 
            label: (
                <IconWithTooltip title="Sale Comps" count={saleCompCount}>
                    <SaleCompIcon color="#25479A" />
                </IconWithTooltip>
            )
        },
        { 
            value: CompanyTab.Leads, 
            label: (
                <IconWithTooltip title="Leads" count={leadCount}>
                    <LeadIcon color="#25479A" />
                </IconWithTooltip>
            )
        },
        { 
            value: CompanyTab.TenantsInMarket, 
            label: (
                <IconWithTooltip title="Tenants in Market" count={tenantInMarketCount}>
                    <TenantInMarketIcon color="#25479A" />
                </IconWithTooltip>
            )
        },
    ], [locationCount, dealCount, leaseCompCount, saleCompCount, leadCount, tenantInMarketCount]);
    useRecentlyViewed(Number(id), RecentItemType.Company);

    const close = () => navigate("/");
    useClosable(ClosableOrder.Medium, close);

    useEffect(() => {
        return () => popupStore.close();
    }, [company]);

    if (pipedriveStore.isOrganizationsLoading.fetching || locationStore.isLoading)
        return <OnMapSpinner />;

    if (!company)
        return null;

    const headquarters = company.hqCity && company.hqState && `${company.hqCity}, ${company.hqState}`;
    const summary = [
        company.revenue && `${formatPrice(Math.round(company.revenue))}M/yr`,
        company.numberOfEmployees && `${formatNumber(company.numberOfEmployees)} employees`,
    ].filter(Boolean).join(", ");

    return (
        <DetailsPanel type={DetailsType.CompanyDetails} onClose={close} controls={[
            <CompanyFavoriteCheckbox key="favorite" id={Number(id)} className={commonStyles.clickableIcon} />,
            <EditIcon key="edit" onClick={() => navigate(`/companies/${id}/edit`)} />
        ]}>
            <div className={detailsStyles.header}>
                <div className={detailsStyles.headerGroup}>
                    {companyLogo
                        ? <img src={companyLogo.downloadUrl} alt={company.name} width={24} height={24} style={{ objectFit: "contain" }} />
                        : <CompanyIcon color="#25479A" />
                    }
                    <div className={detailsStyles.title}>
                        <span className={commonStyles.primary}>{company.name}{!!headquarters && ` (${headquarters})`}</span>
                        {!!summary && <div>{summary}</div>}
                    </div>
                </div>
                <div className={detailsStyles.headerGroup}>
                    <ButtonWithTooltip title="Pipedrive" className={commonStyles.navigationButton} onClick={() => window.open(pipedriveStore.getLinkToOrganization(company.id))}>
                        <PipedriveIcon color="#25479A" />
                    </ButtonWithTooltip>
                    {!!company.linkedInProfile && (
                        <ButtonWithTooltip title="LinkedIn" className={commonStyles.navigationButton} onClick={() => window.open(withHttps(company.linkedInProfile!))}>
                            <LinkedInIcon />
                        </ButtonWithTooltip>
                    )}
                    {!!company.website && (
                        <ButtonWithTooltip title="Website" className={commonStyles.navigationButton} onClick={() => window.open(withHttps(company.website!))}>
                            <LinkIcon color="#202020" />
                        </ButtonWithTooltip>
                    )}
                </div>
            </div>
            <SegmentedButtons
                value={companyTab}
                onChange={setCompanyTab}
                options={segmentedButtons}
                className={detailsStyles.segmentedButtons}
            />
            <div className={detailsStyles.body}>
                <CompanyDetailsLocationsTab visible={companyTab === CompanyTab.Locations} setEntityCount={setLocationCount} />
                <CompanyDetailsDealsTab visible={companyTab === CompanyTab.Deals} setEntityCount={setDealCount} />
                <CompanyDetailsLeaseCompsTab visible={companyTab === CompanyTab.LeaseComps} setEntityCount={setLeaseCompCount} />
                <CompanyDetailsSaleCompsTab visible={companyTab === CompanyTab.SaleComps} setEntityCount={setSaleCompCount} />
                <CompanyDetailsLeadsTab visible={companyTab === CompanyTab.Leads} setEntityCount={setLeadCount} />
                <CompanyDetailsTenantsInMarketTab visible={companyTab === CompanyTab.TenantsInMarket} setEntityCount={setTenantInMarketCount} />
            </div>
        </DetailsPanel>
    );
}

export default observer(CompanyDetails);

enum CompanyTab {
    Locations,
    Deals,
    LeaseComps,
    SaleComps,
    Leads,
    TenantsInMarket,
}